import { useCallback, useRef, useState } from 'react';

import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import { ImageLogo } from '~/assets/images';
import { Input } from '~/components';
import api from '~/services/api';
import getValidationErrors from '~/utils/getValidationErrors';

import Modal from './Modal';

import {
  Container,
  Content,
  FormButton,
  ModalContainer,
  ModalTitle,
  RememberedContainer,
  Title,
} from './styles';

type RecoverPasswordProps = {
  email: string;
  cpf: string;
};

type CodeProps = {
  code: string;
};

type PasswordProps = {
  password: string;
};

type User = {
  email: string;
  id: number;
};

export default function RecoverPassword() {
  const { goBack, go, push } = useHistory();
  const [user, setUser] = useState({} as User);
  const [isRecovering, setIsRecovering] = useState(false);

  function FirstScreen() {
    const formRef = useRef<FormHandles>(null);
    const [loading, setLoading] = useState(false);

    const handleSubmit = useCallback(
      async (formFields: RecoverPasswordProps) => {
        setLoading(true);

        const schema = Yup.object().shape({
          email: Yup.string()
            .required('E-mail é obrigatório!')
            .email('Digite um e-mail válido!'),
          // cpf: Yup.string().required('CPF é obrigatório!'),
        });

        try {
          await schema.validate(formFields, { abortEarly: false });

          const { data } = await api.post('auth/forgot-password', {
            email: formFields.email,
          });

          if (data) {
            setIsRecovering(true);
            setLoading(false);
            setUser(data.data);
          }
        } catch (err) {
          setLoading(false);

          if (err instanceof Yup.ValidationError) {
            const errors = getValidationErrors(err);

            formRef.current?.setErrors(errors);
            console.log(errors);
          } else {
            toast.error('Ocorreu um erro tente novamente!');
          }
        }
      },
      [],
    );

    return (
      <Container>
        <Content>
          <ImageLogo />

          <Title>Recupere sua senha</Title>

          <Form ref={formRef} onSubmit={handleSubmit}>
            <Input name="email" placeholder="E-mail" type="email" />
            {/* <InputMask name="cpf" mask="999.999.999-99" placeholder="CPF" /> */}
          </Form>

          <FormButton
            disabled={loading}
            onClick={() => formRef.current?.submitForm()}
          >
            {loading ? 'Carregando...' : 'Recuperar'}
          </FormButton>

          <RememberedContainer>
            Lembrou sua senha?&nbsp;
            <button type="button" onClick={() => push('/professional/sign-in')}>
              Entre agora!
            </button>
          </RememberedContainer>
        </Content>
      </Container>
    );
  }

  function SecondScreen() {
    const formRef = useRef<FormHandles>(null);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);

    function ModalComponent() {
      const newPasswordFormRef = useRef<FormHandles>(null);

      const [passwordLoading, setPasswordLoading] = useState(false);
      const [isPasswordSubmitted, setIsPasswordSubmitted] = useState(false);

      const handlePasswordSubmit = useCallback(
        async (formFields: PasswordProps) => {
          setPasswordLoading(true);

          const schema = Yup.object().shape({
            password: Yup.string().required('Nova senha é obrigatório'),
          });

          try {
            await schema.validate(formFields, {
              abortEarly: false,
            });

            const { data } = await api.post('/auth/reset-password', {
              password: formFields.password,
              id: user.id,
            });

            if (data) {
              setIsPasswordSubmitted(true);
            }
          } catch (err) {
            setPasswordLoading(false);

            if (err instanceof Yup.ValidationError) {
              const errors = getValidationErrors(err);

              newPasswordFormRef.current?.setErrors(errors);
              console.log(errors);
            } else {
              toast.error('Ocorreu um erro tente novamente!');
            }
          }
        },
        [],
      );

      return (
        <Modal isOpen={showModal} maxWidthStyled="90%" maxHeightStyled="80%">
          <ModalContainer>
            {isPasswordSubmitted ? (
              <>
                <ModalTitle>Nova senha cadastrada!</ModalTitle>
                <p>Volte a realizar o seu login.</p>
                <button type="button" onClick={() => goBack()}>
                  Voltar
                </button>
              </>
            ) : (
              <>
                <ModalTitle>Insira uma nova senha abaixo:</ModalTitle>

                <Form ref={newPasswordFormRef} onSubmit={handlePasswordSubmit}>
                  <Input
                    name="password"
                    placeholder="Nova senha"
                    showIconPassword
                  />
                </Form>

                <FormButton
                  disabled={passwordLoading}
                  onClick={() => newPasswordFormRef.current?.submitForm()}
                >
                  Recuperar
                </FormButton>
              </>
            )}
          </ModalContainer>
        </Modal>
      );
    }

    const handleSubmit = useCallback(async (formFields: CodeProps) => {
      setLoading(true);

      const schema = Yup.object().shape({
        code: Yup.string()
          .min(6, 'O código deve ter 6 dígitos!')
          .max(6, 'O código deve ter 6 dígitos!')
          .required('O campo deve ser preenchido!'),
      });

      try {
        await schema.validate(formFields, { abortEarly: false });

        const { data } = await api.post('auth/validate-code', {
          email: user.email,
          code: formFields.code,
        });

        if (data) {
          setShowModal(true);
        }
      } catch (err) {
        setLoading(false);

        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);

          formRef.current?.setErrors(errors);
          console.log(errors);
        } else {
          toast.error('Ocorreu um erro tente novamente!');
        }
      }
    }, []);

    return (
      <Container>
        <ModalComponent />

        <Content>
          <ImageLogo />

          <Title secondary>Insira o código recebido em seu e-mail:</Title>

          <Form ref={formRef} onSubmit={handleSubmit}>
            <Input name="code" />
          </Form>

          <FormButton onClick={() => formRef.current?.submitForm()}>
            {loading ? 'Validando...' : 'validar código'}
          </FormButton>

          <RememberedContainer>
            Caso não receba dentro de 2 minutos&nbsp; <br />
            <button type="button" onClick={() => go(0)}>
              solicite um novo.
            </button>
          </RememberedContainer>
        </Content>
      </Container>
    );
  }

  return <>{isRecovering ? <SecondScreen /> : <FirstScreen />}</>;
}
